import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { usePatientMutations } from '@/features/patient-record/hooks/usePatientMutations';
import { formatAddress } from '@/lib/format';
import type { AddressViewModel, DemographicsViewModel } from '@/models/patient';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../../_shared/CollapsibleCard';
import { ConfirmDeleteDialog } from '../../_shared/ConfirmDeleteDialog';

import { AddressEditor } from './AddressEditor';
import { AddressRow } from './AddressRow';

const EMPTY_ADDRESS: AddressViewModel = {
  line: null,
  line2: null,
  city: null,
  district: null,
  province: null,
  postalCode: null,
  country: null,
};

interface AddressesCardProps {
  readonly guid: string;
  readonly demographics: DemographicsViewModel;
}

export function AddressesCard({ guid, demographics }: AddressesCardProps): ReactElement {
  const { t } = useTranslation();
  const { updateDemographics } = usePatientMutations(guid);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const persist = (addresses: readonly AddressViewModel[]): void => {
    updateDemographics.mutate({ ...demographics, addresses });
  };

  const saveEdit = (index: number, next: AddressViewModel): void => {
    const list = demographics.addresses.map((entry, i) => (i === index ? next : entry));
    persist(list);
    setEditingIndex(null);
  };

  const saveNew = (next: AddressViewModel): void => {
    persist([...demographics.addresses, next]);
    setCreating(false);
  };

  const showEmpty = demographics.addresses.length === 0 && !creating;

  const summaryRows: CollapsibleCardSummaryRow[] = demographics.addresses.map((address, index) => {
    const hasAnyPart = [
      address.line,
      address.line2,
      address.city,
      address.district,
      address.province,
      address.postalCode,
      address.country,
    ].some((part) => part !== null && part.length > 0);
    return {
      label: `${t('patient.demographics.addressLabel', { defaultValue: 'Address' })} ${String(index + 1)}`,
      value: formatAddress(address),
      filled: hasAnyPart,
    };
  });

  return (
    <>
      <CollapsibleCard
        idPrefix="demographics"
        anchorId="addresses"
        title={t('patient.demographics.addresses')}
        count={demographics.addresses.length}
        summary={summaryRows}
      >
        {showEmpty ? (
          <p className="text-sm text-muted-foreground italic mb-3">
            {t('patient.demographics.noAddresses')}
          </p>
        ) : (
          <ul className="mb-3">
            {demographics.addresses.map((address, index) => (
              <li key={`address-${String(index)}`}>
                {editingIndex === index ? (
                  <AddressEditor
                    address={address}
                    onSave={(next) => {
                      saveEdit(index, next);
                    }}
                    onCancel={() => {
                      setEditingIndex(null);
                    }}
                  />
                ) : (
                  <AddressRow
                    address={address}
                    onEdit={() => {
                      setEditingIndex(index);
                    }}
                    onDelete={() => {
                      setPendingDeleteIndex(index);
                    }}
                  />
                )}
              </li>
            ))}
            {creating && (
              <li>
                <AddressEditor
                  address={EMPTY_ADDRESS}
                  onSave={saveNew}
                  onCancel={() => {
                    setCreating(false);
                  }}
                />
              </li>
            )}
          </ul>
        )}
        {!creating && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCreating(true);
              setEditingIndex(null);
            }}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('patient.demographics.addAddress')}
          </Button>
        )}
      </CollapsibleCard>
      {pendingDeleteIndex !== null && (
        <ConfirmDeleteDialog
          title={t('patient.demographics.confirmDeleteAddressTitle')}
          message={t('patient.demographics.confirmDeleteAddressMessage')}
          onCancel={() => {
            setPendingDeleteIndex(null);
          }}
          onConfirm={() => {
            const list = demographics.addresses.filter((_, i) => i !== pendingDeleteIndex);
            persist(list);
            setPendingDeleteIndex(null);
          }}
        />
      )}
    </>
  );
}
